import type { ComponentProps } from "react";
import { cn } from "../lib/cn";
import { shortcutKeys, type Platform } from "../lib/shortcut";

const keyClassName =
  "inline-flex h-5 min-w-5 items-center justify-center rounded-sm border border-border bg-surface px-1 font-sans text-[11px] font-medium text-fg-muted";

export interface KbdProps extends Omit<ComponentProps<"kbd">, "children"> {
  /** A combo like `"mod+k"`; each key renders as its own cap, platform-aware. */
  keys?: string;
  /** Overrides platform detection (mainly for tests and previews). */
  platform?: Platform;
  /** Literal content when `keys` is not given. */
  children?: React.ReactNode;
}

/** Renders keyboard keys, e.g. `<Kbd keys="mod+k" />` shows ⌘ K on a Mac and Ctrl K elsewhere. */
export function Kbd({ keys, platform, className, children, ...props }: KbdProps) {
  if (keys === undefined) {
    return (
      <kbd data-slot="kbd" className={cn(keyClassName, className)} {...props}>
        {children}
      </kbd>
    );
  }
  if (keys.trim().includes(" ")) {
    const chords = [];
    let prefix = "";
    for (const chord of keys.trim().split(/\s+/)) {
      const first = prefix === "";
      prefix += ` ${chord}`;
      chords.push({ id: prefix, chord, first });
    }
    return (
      <span className={cn("inline-flex items-center gap-1", className)}>
        {chords.map(({ id, chord, first }) => (
          <span key={id} className="inline-flex items-center gap-1">
            {!first ? (
              <span aria-label="then" className="text-fg-faint">
                →
              </span>
            ) : null}
            <Kbd keys={chord} {...(platform ? { platform } : {})} />
          </span>
        ))}
      </span>
    );
  }
  const labels = platform === undefined ? shortcutKeys(keys) : shortcutKeys(keys, platform);
  return (
    <span data-slot="kbd-group" className={cn("inline-flex items-center gap-0.5", className)}>
      {labels.map((label, index) => (
        // Keys can repeat within one combo; position keeps the key unique.
        <kbd key={`${label}-${index}`} data-slot="kbd" className={keyClassName} {...props}>
          {label}
        </kbd>
      ))}
    </span>
  );
}
